import React, { useCallback, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { URL_NAVS } from '@/constants';
import { userUpdateComplaintStatusInPropertyDetails } from '@/data/data-actions';
import { AsyncRsGrid } from '@/list/components/async-rs-grid';
import { InFlightValue } from '@/list/components/cells/in-flight-value';
import { GridWrapper } from '@/list/components/rs-grid/rs-grid-styles';
import * as userSelectors from '@/user/user-selectors';
import { getPlaceSpecificUrl } from '@/utils/place-detection';
import { useComplaintsStatus } from '@/utils/useComplaintsStatus';
import styled from 'styled-components';
import { ComplaintsColDefs } from '../complaints-col-defs';

const Grid = styled(AsyncRsGrid)`
  ${GridWrapper} & {
    height: 75vh !important;
  }
`;

function SummarizedComplaintsListComponent(props) {
  const history = useHistory();

  const currentPlace = useSelector(userSelectors.currentCity);

  const { updateComplaintStatus } = props;

  const { allStatusDescriptions, complaintStatusLabelFactory } =
    useComplaintsStatus();

  const gridData = useMemo(
    () => InFlightValue.wrapInFlightRows(props.complaints || []),
    [props.complaints]
  );

  const onStatusCellValueChange = useCallback(
    ({ rowId, cellValue: status }) => {
      const complaintId = parseInt(rowId, 10);
      const selectedComplaint = gridData.find(
        (complaint) => complaintId === complaint.complaintId
      );
      updateComplaintStatus(selectedComplaint, status);
    },
    [updateComplaintStatus, gridData]
  );

  const complaintsColDefFactory = useMemo(
    () =>
      new ComplaintsColDefs(onStatusCellValueChange, {
        allStatusDescriptions,
        complaintStatusLabelFactory,
      }),
    [
      onStatusCellValueChange,
      allStatusDescriptions,
      complaintStatusLabelFactory,
    ]
  );

  const { defaultColDef } = complaintsColDefFactory;

  const colDefs = useMemo(
    () => complaintsColDefFactory.generateSummarizedAgGridColDefs(),
    [complaintsColDefFactory]
  );

  const onRowClicked = useCallback(
    (e) => {
      const complaintsListViewRoute = getPlaceSpecificUrl(
        currentPlace,
        `/${URL_NAVS.complaints}/${e.data.complaintId}`
      );

      history.push(complaintsListViewRoute);
    },
    [currentPlace, history]
  );

  return (
    <Grid
      // Prevent the grid from scrolling back to the top when `complaintsData` changes
      immutableData
      // Unique ids are needed by `immutableData` to reconcile updates to `complaintsData`
      // We also need node ids to be complaintIds
      // to handle the change callback from EditableDropdownCell
      getRowNodeId={(complaint) => complaint.complaintId}
      columnDefs={colDefs}
      rowData={gridData}
      defaultColDef={defaultColDef}
      sideBar={false}
      onRowClicked={onRowClicked}
    />
  );
}

function mapDispatchToProps(dispatch) {
  return {
    updateComplaintStatus: (complaint, status) =>
      dispatch(
        userUpdateComplaintStatusInPropertyDetails({
          complaint,
          status,
        })
      ),
  };
}

export const SummarizedComplaintsList = connect(
  null,
  mapDispatchToProps
)(SummarizedComplaintsListComponent);
